import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { db } from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaWhatsapp, FaGoogle, FaGlobe, FaLayerGroup } from 'react-icons/fa';

const { FiCheckCircle, FiAlertTriangle } = FiIcons;

const SetupScreen = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState('direct');
  const [url, setUrl] = useState('');
  
  // Profile State
  const [profile, setProfile] = useState({
    company: '',
    logoUrl: '',
    bio: '',
    links: []
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!state?.tag_code) {
      navigate('/');
      return;
    }

    const init = async () => {
      // Check if it's already configured
      if (state.link_type === 'smart') {
        setMode('smart');
        setProfile(prev => ({
          ...prev,
          company: state.company_name || '',
          logoUrl: state.logo_url || '',
          bio: state.bio || ''
        }));
        
        // Fetch existing links
        const links = await db.getProfileLinks(state.tag_code);
        const paddedLinks = [...links];
        while (paddedLinks.length < 6) {
          paddedLinks.push({ label: '', url: '' });
        }
        setProfile(prev => ({ ...prev, links: paddedLinks }));

      } else {
        setMode('direct');
        setUrl(state.redirect_url || '');
        setProfile(prev => ({ ...prev, links: Array(6).fill({ label: '', url: '' }) }));
      }
    };
    init();

  }, [state, navigate]);

  const updateLink = (index, field, value) => {
    const newLinks = [...profile.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setProfile(prev => ({ ...prev, links: newLinks }));
  };

  const validateUrl = (inputUrl) => {
    if (!inputUrl) return null;
    let finalUrl = inputUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    return finalUrl;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');

    try {
      const updates = { is_active: 1 };

      if (mode === 'direct') {
        const validUrl = validateUrl(url);
        if (!validUrl) throw new Error("Please enter a valid URL.");
        
        updates.link_type = 'direct';
        updates.redirect_url = validUrl;
        
        // Clear smart profile fields
        updates.company_name = '';
        updates.logo_url = '';
        updates.bio = '';

      } else {
        if (!profile.company.trim()) throw new Error("Company Name is required.");
        
        updates.link_type = 'smart';
        updates.company_name = profile.company;
        updates.logo_url = profile.logoUrl;
        updates.bio = profile.bio;
        updates.redirect_url = ''; // Clear direct link
      }

      // Update Tag Info
      await db.update(state.id, updates);

      // Handle Profile Links if smart
      if (mode === 'smart') {
        // Filter out empty links but keep IDs if they exist (to handle deletes properly in sync logic if needed, 
        // though our sync logic deletes based on missing IDs in the new list.
        // Wait, if I filter out empty links here, they won't be sent to sync. 
        // If they had an ID, they will be deleted. This is correct behavior for "removing" a link.
        
        const activeLinks = profile.links.filter(l => l.label && l.url).map(l => ({
          ...l,
          url: validateUrl(l.url)
        }));

        if (activeLinks.length === 0) throw new Error("Please add at least one link.");
        
        await db.syncProfileLinks(state.tag_code, activeLinks);
      }

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  if (success) {
    return (
      <Layout>
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6 animate-in fade-in zoom-in duration-300">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
            <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">You're all set!</h2>
            <p className="text-zinc-400">Future scans will open your {mode === 'smart' ? 'Smart Profile' : 'link'} instantly.</p>
          </div>
          <Button onClick={() => window.location.href = window.location.origin + '/#/tag?tag_id=' + state.tag_code}>
            Test Live Card
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div>
          <h1 className="text-3xl font-bold mb-2">Setup Smart Card</h1>
          <p className="text-zinc-400">Choose what happens when people scan your card.</p>
        </div>

        {/* Type Selector */}
        <div className="grid grid-cols-3 gap-2">
          <button 
            onClick={() => setMode('direct')}
            className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${mode === 'direct' ? 'bg-red-600 border-red-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800'}`}
          >
            <SafeIcon icon={FaGlobe} />
            <span className="text-xs font-bold">Direct Link</span>
          </button>
          <button 
            onClick={() => setMode('smart')}
            className={`col-span-2 p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${mode === 'smart' ? 'bg-red-600 border-red-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800'}`}
          >
            <SafeIcon icon={FaLayerGroup} />
            <span className="text-xs font-bold">Smart Profile (Multi-Link)</span>
          </button>
        </div>

        {/* DIRECT LINK MODE */}
        {mode === 'direct' && (
          <div className="space-y-6 animate-in slide-in-from-right-4 fade-in">
             <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                {[
                  { icon: FaFacebook, color: 'text-blue-500', prefix: 'facebook.com/' },
                  { icon: FaInstagram, color: 'text-pink-500', prefix: 'instagram.com/' },
                  { icon: FaWhatsapp, color: 'text-green-500', prefix: 'wa.me/' },
                  { icon: FaGoogle, color: 'text-white', prefix: 'google.com' },
                ].map((item, i) => (
                  <button 
                    key={i}
                    onClick={() => setUrl(item.prefix)}
                    className="flex-shrink-0 w-12 h-12 bg-zinc-900 border border-zinc-800 rounded-full flex items-center justify-center hover:border-zinc-600 transition-colors"
                  >
                    <SafeIcon icon={item.icon} className={`text-xl ${item.color}`} />
                  </button>
                ))}
             </div>
             <Input label="Destination URL" placeholder="example.com" value={url} onChange={(e) => { setUrl(e.target.value); setError(''); }} error={error} />
          </div>
        )}

        {/* SMART PROFILE MODE */}
        {mode === 'smart' && (
          <div className="space-y-6 animate-in slide-in-from-right-4 fade-in">
            <div className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800 space-y-4">
              <Input 
                label="Company / Brand Name"
                placeholder="e.g. Acme Corp"
                value={profile.company}
                onChange={(e) => setProfile({...profile, company: e.target.value})}
              />
              <Input 
                label="Logo Image URL (Optional)"
                placeholder="https://example.com/logo.png"
                value={profile.logoUrl || ''}
                onChange={(e) => setProfile({...profile, logoUrl: e.target.value})}
              />
              <Input 
                label="Bio / Tagline"
                placeholder="e.g. Innovation for future."
                value={profile.bio}
                onChange={(e) => setProfile({...profile, bio: e.target.value})}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-400 mb-2 block">Your Links (Max 6)</label>
              <div className="space-y-3">
                {profile.links.map((link, i) => (
                  <div key={i} className="flex gap-2 items-start bg-zinc-900/30 p-3 rounded-xl border border-zinc-800/50">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-zinc-800 text-zinc-500 text-xs font-bold mt-1">
                      {i + 1}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input 
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-600 border-b border-zinc-800 focus:border-red-500 outline-none py-1 transition-colors"
                        placeholder="Link Title (e.g. Website)"
                        value={link.label}
                        onChange={(e) => updateLink(i, 'label', e.target.value)}
                      />
                      <input 
                        className="w-full bg-transparent text-xs text-zinc-400 placeholder-zinc-700 outline-none font-mono py-1"
                        placeholder="https://..."
                        value={link.url}
                        onChange={(e) => updateLink(i, 'url', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
           <div className="text-center p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-xs flex items-center justify-center gap-2">
             <SafeIcon icon={FiAlertTriangle} /> {error}
           </div>
        )}

        <div className="pt-4">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default SetupScreen;